import { NO_PROXY } from '@hit-game/shared/constants';
import type { IAccountDataResponse } from '@hit-game/shared/types';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Copy, Repeat, Settings2, Shield } from 'lucide-react';
import { useState } from 'react';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { formatSignedMoney } from '../../utils/format';
import { winRateColor, NO_DATA_COLOR } from './winRate';

interface AccountCardMetaProps {
  account: IAccountDataResponse;
}

function StatItem({ label, value }: { label: string; value: number | '?' | undefined }) {
  const numeric = typeof value === 'number' ? value : 0;
  const unknown = value === '?' || value == null;

  let color = 'text-ink-dim';
  if (!unknown && numeric > 0) color = 'text-success';
  else if (!unknown && numeric < 0) color = 'text-danger';

  return (
    <div className="min-w-0 text-center">
      <span className="text-ink-dim block text-[9px] font-bold uppercase tracking-wider">
        {label}
      </span>
      <span className={`mt-0.5 block text-xs font-bold ${color}`}>{formatSignedMoney(value)}</span>
    </div>
  );
}

/**
 * Khối chìm ở chân thẻ — GẬP mặc định, chỉ còn một dòng tóm tắt (số ván + proxy)
 * và nút "Chi tiết". Bấm mới xổ: nạp/rút, lãi lỗ, cấu hình cược, tỷ lệ thắng, proxy.
 *
 * Mỗi nhóm 4 thẻ mà thẻ nào cũng bày hết thì sảnh dài lê thê; những thứ này chỉ
 * cần khi soi một tài khoản cụ thể.
 */
export function AccountCardMeta({ account }: AccountCardMetaProps) {
  const [copied, copy] = useCopyToClipboard();
  const [open, setOpen] = useState(false);

  const total = account.rounds.total;
  const winRate = total > 0 ? (account.rounds.w / total) * 100 : 0;
  const barColor = winRateColor(winRate);
  const proxyHost = account.proxy === NO_PROXY ? NO_PROXY : account.proxy.split(':')[0];
  const proxyDead = account.proxyAlive === false;

  return (
    <>
      <div className="border-line border-t" />

      <button
        type="button"
        onClick={() => {
          setOpen((value) => !value);
        }}
        aria-expanded={open}
        className="bg-inset hover:bg-panel-hover flex w-full items-center justify-between gap-2 px-4 py-2 text-left transition-colors"
      >
        <span className="flex min-w-0 items-center gap-2 overflow-hidden">
          <Repeat className="text-ink-ghost h-3 w-3 shrink-0" />
          <span
            className="shrink-0 whitespace-nowrap font-mono text-[11px]"
            style={{ color: total > 0 ? barColor : NO_DATA_COLOR }}
          >
            {total}({account.rounds.w}W/{account.rounds.l}L)
          </span>
          <span
            className={`min-w-0 truncate font-mono text-[11px] ${proxyDead ? 'text-danger' : 'text-ink-subtle'}`}
          >
            {proxyHost}
            {proxyDead && ' (chết)'}
          </span>
        </span>
        <span className="text-ink-dim flex shrink-0 items-center gap-1 text-[10px] font-semibold">
          Chi tiết
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="bg-inset overflow-hidden"
          >
            <div className="px-4 pb-3">
              <div className="border-inset-line grid grid-cols-2 gap-4 border-b py-3">
                <StatItem label="Tổng nạp" value={account.totalDeposit} />
                <StatItem label="Rút" value={account.totalWithdraw} />
              </div>
              <div className="border-inset-line grid grid-cols-2 gap-4 border-b py-3">
                <StatItem label="LN Game" value={account.inGameProfit} />
                <StatItem label="LN Thực" value={account.actualProfit} />
              </div>

              <div className="border-inset-line flex items-start gap-2 border-b py-2">
                <Settings2 className="text-ink-ghost mt-[7px] h-[13px] w-[13px] shrink-0" />
                <span className="border-inset-border bg-inset-chip text-accent-bright min-w-0 flex-1 break-all rounded-md border px-2.5 py-1 font-mono text-[12px] leading-[1.6]">
                  {account.config || 'Chưa cấu hình cược'}
                </span>
              </div>

              <div className="border-inset-line flex items-center gap-2 border-b py-2">
                <Repeat className="text-ink-ghost h-[13px] w-[13px] shrink-0" />
                {total > 0 && (
                  <div className="bg-inset-track h-[3px] w-20 shrink-0 overflow-hidden rounded-[2px]">
                    <div
                      className="h-full rounded-[2px] transition-all duration-300"
                      style={{ width: `${String(winRate)}%`, backgroundColor: barColor }}
                    />
                  </div>
                )}
                <span
                  className="whitespace-nowrap font-mono text-[12px]"
                  style={{ color: total > 0 ? barColor : NO_DATA_COLOR }}
                >
                  {total}({account.rounds.w}W/{account.rounds.l}L){' '}
                  {total > 0 ? `${String(Math.round(winRate))}%` : account.rounds.rate}
                </span>
              </div>

              <div className="flex items-center gap-2 py-2">
                <Shield className="text-ink-ghost h-[13px] w-[13px] shrink-0" />
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    account.proxy === NO_PROXY
                      ? 'bg-ink-ghost'
                      : proxyDead
                        ? 'bg-danger'
                        : 'bg-success'
                  }`}
                />
                <span
                  className={`min-w-0 truncate font-mono text-xs ${proxyDead ? 'text-danger' : 'text-ink-subtle'}`}
                  title={proxyDead ? `${account.proxy} — proxy không phản hồi` : account.proxy}
                >
                  {proxyHost}
                  {proxyDead && ' (chết)'}
                </span>
                {account.proxy !== NO_PROXY && (
                  <button
                    type="button"
                    onClick={() => {
                      copy(account.proxy);
                    }}
                    className="text-ink-ghost hover:text-accent-bright shrink-0 p-0.5 transition-colors"
                    title={copied ? 'Đã sao chép' : 'Sao chép proxy'}
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
